import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { z } from 'zod';
import { useMemo } from 'react';
import {
  Bookmark,
  CreditCard,
  Database,
  Factory,
  Globe,
  LineChart,
  ListChecks,
  Network,
  SearchCheck,
  ShieldCheck,
  ThumbsUp,
} from 'lucide-react';

import type { ReportSchema } from '@ballerine/common';
import type { CustomerFeatures } from '@ballerine/customer-schemas';
import type { HighRiskVerticalAnalysisResult } from '@ballerine/digital-footprint-schemas';
import { DIGITAL_FOOTPRINT_MONITORING_REPORT_TYPES } from '@ballerine/common';

import type { BallerineNetworkInsightsData } from '../../sections/BallerineNetworkInsights';
import { RegistryDataSection, RegistryInformationVendor } from '../../../../index';
import {
  BallerineNetworkInsightsSection,
  CompliancePagesSection,
  ContentFindingsSection,
  DomainDetailsSection,
  EcosystemSection,
  HeaderSection,
  HighRiskVerticalAnalysisSection,
  MerchantCategoryCodesSection,
  MerchantDetailsSection,
  ReferencesSection,
  RiskAnalysisSection,
  SocialMediaSection,
  TagsSection,
  TrafficAnalysisSection,
  YourPolicyFitSection,
} from '../../sections';

type DigitalFootprintReportSection = {
  id: string;
  title: string;
  description?: string;
  Icon?: LucideIcon;
  Component: ReactNode;
  hasViolations?: boolean;
  isPremium?: boolean;
  excludeFromObserver?: boolean;
  isHidden: boolean;
};

const hasHighRiskVerticalContent = (result: HighRiskVerticalAnalysisResult) => {
  const hasSummary = Boolean(result.summary?.trim());
  const hasSections =
    Array.isArray(result.sections) &&
    result.sections.some(section => Boolean(section.content.trim()));

  return hasSummary || hasSections;
};

type SimpleTag = {
  name: string;
  level: 'positive' | 'moderate' | 'negative';
};

const getNetworkInsightsTag = (networkInsights: BallerineNetworkInsightsData): SimpleTag => {
  const status = networkInsights?.status ?? 'clear';

  switch (status) {
    case 'clear':
      return {
        name: 'No Off-Boarding Reported',
        level: 'positive',
      };
    case 'off_boarding_reported':
      return {
        name: 'Off-Boarding Reported',
        level: 'moderate',
      };
    case 'high_risk_off_boarding_reported':
      return {
        name: 'High Risk Off-Boarding Reported',
        level: 'negative',
      };
    default:
      return {
        name: 'No Off-Boarding Reported',
        level: 'positive',
      };
  }
};

const getTagsWithNetworkInsights = (
  tags: SimpleTag[] | null | undefined,
  networkInsights: BallerineNetworkInsightsData,
  isEnabled: boolean,
): SimpleTag[] | undefined => {
  if (!isEnabled || !networkInsights) return tags ?? undefined;

  const networkInsightsTag = getNetworkInsightsTag(networkInsights);
  const baseTags = tags ?? [];

  return [networkInsightsTag, ...baseTags];
};

export const useDigitalFootprintReportSections = (
  report: z.infer<typeof ReportSchema>,
  customerFeatures?: CustomerFeatures | null,
  googleMapsApiKey?: string,
  FeedbackComponent?: React.ComponentType<{ reportId: string }>,
  locale?: string,
  onOpenOriginalPolicy?: () => void,
) => {
  const {
    homePageScreenshotUrl,
    riskLevel,
    summary,
    ongoingMonitoringSummary,
    contentRiskIndicators,
    websiteStructureRiskIndicators,
    lineOfBusiness,
    mcc: mccSummary,
    trafficSources,
    visitorsCountries,
    monthlyVisits,
    pagesPerVisit,
    timeOnSite,
    bounceRate,
    ecosystem,
    facebookPage,
    instagramPage,
    twitterPage,
    linkedinPage,
    tiktokPage,
    domainInfo,
    tags,
    references,
    riskTypes,
    policyFit,
    merchantDetails,
    highRiskVerticalAnalysisResults,
    companyRegistryData,
    gatedContentCheck,
    ballerineNetworkInsights,
  } = report.data ?? {};

  // Use edited homepage screenshot if available, fallback to gated content screenshot
  const effectiveHomepageScreenshotUrl = homePageScreenshotUrl ?? gatedContentCheck?.screenshotUrl;

  const companyRegistryCheckEnabled =
    (customerFeatures?.DIGITAL_FOOTPRINT?.enableCompanyRegistryCheck ?? false) &&
    (report.declaredDetails?.companyRegistryCheck?.enabled ?? false);
  const ballerineNetworkInsightsEnabled =
    customerFeatures?.DIGITAL_FOOTPRINT?.enableBallerineNetworkInsights ?? false;

  const isAupEnabled = customerFeatures?.AUP?.enabled ?? false;
  const isCardSchemeContentRiskEnabled =
    customerFeatures?.DIGITAL_FOOTPRINT?.enableCardSchemeContentRisk ?? false;

  const isMonitoringReport = (
    DIGITAL_FOOTPRINT_MONITORING_REPORT_TYPES as readonly string[]
  ).includes(report.reportType);

  const geographies = visitorsCountries?.data ? Object.keys(visitorsCountries.data) : undefined;

  const sections = useMemo(() => {
    const validHighRiskVerticalResults: HighRiskVerticalAnalysisResult[] = Array.isArray(
      highRiskVerticalAnalysisResults,
    )
      ? (highRiskVerticalAnalysisResults as HighRiskVerticalAnalysisResult[]).filter(
          hasHighRiskVerticalContent,
        )
      : [];

    const previousReportsLinks =
      report.previousReports && locale
        ? {
            previousMonitoringReportHref: report.previousReports.previousMonitoringReportId
              ? `/${locale}/digital-footprint/${report.previousReports.previousMonitoringReportId}`
              : null,
            onboardingReportHref: report.previousReports.onboardingReportId
              ? `/${locale}/digital-footprint/${report.previousReports.onboardingReportId}`
              : null,
          }
        : null;

    const cardSchemeRiskFindings = (contentRiskIndicators ?? [])
      .filter(indicator => indicator.cardSchemeContentRisk === true)
      .map(indicator => ({ id: indicator.id, name: indicator.name }));

    const allSections = [
      {
        id: 'header',
        Icon: Globe,
        excludeFromObserver: true,
        isHidden: false,
        Component: (
          <HeaderSection
            reportId={report.id}
            homepageScreenshotUrl={effectiveHomepageScreenshotUrl}
            riskLevel={riskLevel}
            lineOfBusiness={lineOfBusiness}
            mcc={mccSummary}
            geographies={geographies}
            riskTypes={riskTypes}
            policyFit={policyFit}
            customer={report.customer}
            isPolicyFitEnabled={!!policyFit && isAupEnabled}
            isCardSchemeContentRiskEnabled={isCardSchemeContentRiskEnabled}
            cardSchemeRiskFindings={cardSchemeRiskFindings}
            isMonitoringReport={isMonitoringReport}
            isAlert={report.isAlert ?? report.data?.isAlert ?? false}
            FeedbackComponent={FeedbackComponent}
            reportType={report.reportType}
            onOpenOriginalPolicy={onOpenOriginalPolicy}
          />
        ),
      },
      {
        id: 'your-policy-fit',
        isHidden: !policyFit || !isAupEnabled,
        Icon: ThumbsUp,
        excludeFromObserver: true,
        Component: (
          <YourPolicyFitSection
            policyFit={policyFit}
            customer={report.customer}
            onOpenOriginalPolicy={onOpenOriginalPolicy}
          />
        ),
      },
      {
        id: 'tags',
        isHidden: !tags?.length,
        Icon: ListChecks,
        excludeFromObserver: true,
        Component: (
          <TagsSection
            tags={getTagsWithNetworkInsights(
              tags,
              ballerineNetworkInsights as BallerineNetworkInsightsData,
              ballerineNetworkInsightsEnabled,
            )}
          />
        ),
      },
      {
        id: 'risk-analysis',
        title: 'Risk Analysis',
        isHidden: summary === undefined,
        Icon: ListChecks,
        Component: (
          <RiskAnalysisSection
            summary={summary}
            createdAt={report.createdAt}
            ongoingMonitoringSummary={ongoingMonitoringSummary}
            homepageScreenshotUrl={effectiveHomepageScreenshotUrl}
            gatedContentScreenshotUrl={gatedContentCheck?.screenshotUrl}
            previousReportsLinks={previousReportsLinks}
            isAlert={report.isAlert ?? report.data?.isAlert ?? false}
            isMonitoringReport={isMonitoringReport}
          />
        ),
      },
      {
        id: 'ballerine-network-insights',
        title: "Ballerine's Network",
        isHidden: !ballerineNetworkInsightsEnabled || !ballerineNetworkInsights,
        Icon: Network,
        Component: (
          <BallerineNetworkInsightsSection
            networkInsights={ballerineNetworkInsights as BallerineNetworkInsightsData}
          />
        ),
      },
      {
        id: 'content-findings',
        title: 'Content Findings',
        isHidden: contentRiskIndicators === undefined,
        Icon: SearchCheck,
        Component: <ContentFindingsSection contentRiskIndicators={contentRiskIndicators} />,
      },
      // High risk vertical analysis sections (split IP rights vs others)
      ...(() => {
        if (validHighRiskVerticalResults.length === 0) {
          return [] as DigitalFootprintReportSection[];
        }

        const ipRightsResults = validHighRiskVerticalResults.filter(
          r => r.vertical === 'ip-rights-and-brands',
        );
        const otherHighRiskResults = validHighRiskVerticalResults.filter(
          r => r.vertical !== 'ip-rights-and-brands',
        );

        const sections: DigitalFootprintReportSection[] = [];

        if (otherHighRiskResults.length > 0) {
          sections.push({
            id: 'high-risk-vertical-analysis',
            title: 'High Risk Vertical Analysis',
            Icon: SearchCheck,
            isHidden: false,
            Component: (
              <HighRiskVerticalAnalysisSection
                highRiskVerticalAnalysisResults={otherHighRiskResults}
              />
            ),
          });
        }

        if (ipRightsResults.length > 0) {
          sections.push({
            id: 'ip-rights-and-brands',
            title: 'IP Rights & Brands',
            Icon: SearchCheck,
            isHidden: false,
            Component: (
              <HighRiskVerticalAnalysisSection highRiskVerticalAnalysisResults={ipRightsResults} />
            ),
          });
        }

        return sections;
      })(),
      ...(customerFeatures?.DIGITAL_FOOTPRINT?.enableAlternativeMccSuggestions
        ? [
            {
              id: 'merchant-category-codes',
              title: 'Merchant Category Codes',
              Icon: CreditCard,
              Component: <MerchantCategoryCodesSection mccSummary={mccSummary ?? null} />,
            },
          ]
        : []),
      {
        id: 'merchant-details',
        title: 'Extracted Merchant Details',
        isHidden: merchantDetails === undefined,
        Icon: Factory,
        Component: (
          <MerchantDetailsSection
            merchantDetails={merchantDetails}
            googleMapsApiKey={googleMapsApiKey}
            providedDetails={{
              websiteUrl: report.website?.url,
              companyName: report.companyName,
              merchantId: (report.metadata as Record<string, unknown> | undefined)?.merchantId as
                | string
                | undefined,
              countryCode: (report.metadata as Record<string, unknown> | undefined)?.countryCode as
                | string
                | undefined,
              declaredDetails: report.declaredDetails,
            }}
            detailsComparison={merchantDetails?.detailsComparison}
          />
        ),
      },
      {
        id: 'registry-data',
        title: 'Registry Data',
        isHidden: !companyRegistryCheckEnabled,
        Icon: Database,
        Component: (
          <RegistryDataSection
            input={{
              vendor: RegistryInformationVendor.OPEN_CORPORATES,
              data: companyRegistryData ?? null,
            }}
          />
        ),
      },
      {
        id: 'domain-details',
        title: 'Domain Details',
        isHidden: domainInfo === undefined,
        Icon: Database,
        Component: <DomainDetailsSection domainInfo={domainInfo} />,
      },
      {
        id: 'traffic-analysis',
        title: 'Traffic Analysis',
        isHidden: [
          trafficSources,
          visitorsCountries,
          monthlyVisits,
          pagesPerVisit,
          timeOnSite,
          bounceRate,
        ].every(data => data === undefined),
        Icon: LineChart,
        Component: (
          <TrafficAnalysisSection
            trafficSources={trafficSources}
            visitorsCountries={visitorsCountries}
            monthlyVisits={monthlyVisits}
            pagesPerVisit={pagesPerVisit}
            timeOnSite={timeOnSite}
            bounceRate={bounceRate}
          />
        ),
      },
      {
        id: 'compliance-pages',
        title: 'Compliance Pages',
        isHidden: websiteStructureRiskIndicators === undefined,
        Icon: ShieldCheck,
        Component: (
          <CompliancePagesSection websiteStructureRiskIndicators={websiteStructureRiskIndicators} />
        ),
      },
      ...(!isMonitoringReport
        ? [
            {
              id: 'ecosystem',
              title: 'Ecosystem',
              isHidden: ecosystem === undefined,
              Icon: Globe,
              Component: <EcosystemSection ecosystem={ecosystem} />,
            },
          ]
        : []),
      {
        id: 'social-media',
        title: 'Social Media',
        isHidden: [facebookPage, instagramPage, twitterPage, linkedinPage, tiktokPage].every(
          page => page === undefined,
        ),
        Icon: ThumbsUp,
        Component: (
          <SocialMediaSection
            facebookPage={facebookPage}
            instagramPage={instagramPage}
            twitterPage={twitterPage}
            linkedinPage={linkedinPage}
            tiktokPage={tiktokPage}
          />
        ),
      },
      {
        id: 'references',
        title: 'References',
        isHidden: references === undefined,
        Icon: Bookmark,
        Component: <ReferencesSection references={references} />,
      },
    ] as DigitalFootprintReportSection[];

    return allSections.filter(section => !section.isHidden);
  }, [report, customerFeatures, googleMapsApiKey, FeedbackComponent, locale, onOpenOriginalPolicy]);

  return { sections };
};
